// playshop-card-zoom.js — Modal "Aperçu rapide" pour cards TCG (collection page).
// Click sur loupe → clone le template caché de la card dans un overlay fixed.
// Boutons prev/next + flèches clavier ← → pour naviguer entre cards adjacentes.
//
// UX :
//   - Premier open : backdrop + inner fade-in
//   - Navigation prev/next : overlay PERSISTANT, seul l'inner se cross-fade (smooth)
//   - Préchargement agressif des images voisines (cache navigateur)

(function () {
  'use strict';

  let currentOverlay = null;
  let currentCard = null;
  const preloaded = new Set();
  let swapInProgress = false;

  function closeOverlay() {
    if (!currentOverlay) return;
    currentOverlay.remove();
    currentOverlay = null;
    currentCard = null;
    document.body.style.overflow = '';
  }

  function findAdjacentCard(card, direction) {
    const grid = card.closest('ul, ol, [data-ps-collection-grid]') || card.parentElement;
    if (!grid) return null;
    const allItems = Array.from(grid.children);
    let idx = allItems.indexOf(card.closest('li'));
    if (idx === -1) idx = allItems.indexOf(card);
    let i = idx + direction;
    while (i >= 0 && i < allItems.length) {
      const candidate = allItems[i].querySelector ? allItems[i].querySelector('[data-ps-zoom-content]') : null;
      if (candidate) {
        return allItems[i].matches('.ps-card') ? allItems[i] : allItems[i].querySelector('.ps-card');
      }
      i += direction;
    }
    return null;
  }

  function preloadCardImage(adjacentCard) {
    if (!adjacentCard) return;
    const tpl = adjacentCard.querySelector('[data-ps-zoom-content]');
    if (!tpl) return;
    const tplContent = tpl.content || tpl;
    // Préchargement de TOUTES les <img> dans le template (carte principale + sprite pokédex)
    const imgs = tplContent.querySelectorAll ? tplContent.querySelectorAll('img') : [];
    imgs.forEach((img) => {
      const src = img.getAttribute('src') || img.src;
      if (!src || preloaded.has(src)) return;
      preloaded.add(src);
      const preloader = new Image();
      preloader.src = src;
    });
  }

  function refreshNavButtonsAndPreload(overlay, card) {
    const prevCard = findAdjacentCard(card, -1);
    const nextCard = findAdjacentCard(card, 1);
    const prevBtn = overlay.querySelector('[data-ps-zoom-prev]');
    const nextBtn = overlay.querySelector('[data-ps-zoom-next]');
    if (prevBtn) prevBtn.style.display = prevCard ? '' : 'none';
    if (nextBtn) nextBtn.style.display = nextCard ? '' : 'none';
    // Préchargement voisines + 1 cran de plus
    preloadCardImage(prevCard);
    preloadCardImage(nextCard);
    if (prevCard) preloadCardImage(findAdjacentCard(prevCard, -1));
    if (nextCard) preloadCardImage(findAdjacentCard(nextCard, 1));
  }

  function buildInnerFromCard(card) {
    const template = card.querySelector('[data-ps-zoom-content]');
    if (!template) return null;
    const fragment = template.content ? template.content.cloneNode(true) : template.cloneNode(true);
    // Le contenu cloné contient .ps-zoom__inner en racine du fragment
    return fragment;
  }

  // ── Effet holographique sur la carte du modal (réutilise playshop-holo.js) ──
  // Charge holo.js à la demande (1re ouverture) si pas déjà présent (fiche), puis
  // monte l'effet sur .ps-zoom__media (data-holo posé en Liquid par le snippet).
  function ensureHolo(cb) {
    if (window.PSHolo) { cb(); return; }
    var tag = document.querySelector('script[src*="playshop-holo.js"]');
    if (tag) { tag.addEventListener('load', function () { cb(); }); return; }
    if (!window.PS_HOLO_JS) return;
    var s = document.createElement('script');
    s.src = window.PS_HOLO_JS;
    s.addEventListener('load', function () { cb(); });
    document.head.appendChild(s);
  }
  function applyHolo(scope) {
    if (!scope) return;
    var media = scope.querySelector('.ps-zoom__media[data-holo]');
    if (!media) return;
    ensureHolo(function () {
      if (window.PSHolo && window.PSHolo.apply) window.PSHolo.apply(media);
    });
  }

  function navigateTo(card) {
    if (swapInProgress) return; // Anti-spam (clics rapides)
    if (!currentOverlay) return;
    const newContent = buildInnerFromCard(card);
    if (!newContent) return;
    currentCard = card;
    swapInProgress = true;

    // Fade-out inner actuel
    const oldInner = currentOverlay.querySelector('.ps-zoom__inner');
    if (oldInner) {
      oldInner.classList.add('ps-zoom__inner--exiting');
    }

    // Après 140ms (durée de l'exit anim), on swap
    setTimeout(() => {
      // Vide overlay (sauf si fermé entre-temps)
      if (!currentOverlay) { swapInProgress = false; return; }
      while (currentOverlay.firstChild) currentOverlay.firstChild.remove();
      currentOverlay.appendChild(newContent);
      refreshNavButtonsAndPreload(currentOverlay, card);
      applyHolo(currentOverlay);
      // Le nouvel .ps-zoom__inner joue l'animation entry automatiquement (CSS keyframes)
      const closeBtn = currentOverlay.querySelector('[data-ps-zoom-close]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
      swapInProgress = false;
    }, 140);
  }

  function openOverlay(card) {
    // Si overlay déjà ouvert → c'est une navigation (cross-fade interne)
    if (currentOverlay) { navigateTo(card); return; }

    const newContent = buildInnerFromCard(card);
    if (!newContent) return;
    currentCard = card;

    const overlay = document.createElement('div');
    overlay.className = 'ps-zoom-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Aperçu rapide de la carte');
    overlay.appendChild(newContent);

    // Click handlers (delegated, survivent au swap d'inner)
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) { closeOverlay(); return; }
      if (e.target.closest('[data-ps-zoom-close]')) { closeOverlay(); return; }
      if (e.target.closest('[data-ps-zoom-prev]')) {
        e.preventDefault();
        const p = findAdjacentCard(currentCard, -1);
        if (p) navigateTo(p);
        return;
      }
      if (e.target.closest('[data-ps-zoom-next]')) {
        e.preventDefault();
        const n = findAdjacentCard(currentCard, 1);
        if (n) navigateTo(n);
        return;
      }
    });

    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    currentOverlay = overlay;
    refreshNavButtonsAndPreload(overlay, card);
    applyHolo(overlay);

    const closeBtn = overlay.querySelector('[data-ps-zoom-close]');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }

  // Delegated click handler for triggers (loupes sur les cards)
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-ps-zoom-trigger]');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();
    const card = trigger.closest('.ps-card');
    if (card) openOverlay(card);
  }, true);

  // Keyboard : Escape close, ← prev, → next
  document.addEventListener('keydown', (e) => {
    if (!currentOverlay) return;
    if (e.key === 'Escape') { closeOverlay(); return; }
    if (e.key === 'ArrowLeft') {
      const p = findAdjacentCard(currentCard, -1);
      if (p) { e.preventDefault(); navigateTo(p); }
    } else if (e.key === 'ArrowRight') {
      const n = findAdjacentCard(currentCard, 1);
      if (n) { e.preventDefault(); navigateTo(n); }
    }
  });
})();
